import { useState, useEffect } from "react";
import { X, Plus, ChevronRight, Trash2, Loader2, FolderTree, Pencil, Check, ArrowUpDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface Category {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  created_at: string;
  sort_order?: number;
  children?: Category[];
}

interface CategoryManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CategoryManager = ({ isOpen, onClose }: CategoryManagerProps) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [selectedParent, setSelectedParent] = useState<string>("none");
  const [isSaving, setIsSaving] = useState(false);
  const [deleteCategory, setDeleteCategory] = useState<Category | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editName, setEditName] = useState("");
  const [movingCategory, setMovingCategory] = useState<Category | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name");

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Errore nel caricamento delle categorie");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Inserisci un nome per la categoria");
      return;
    }

    setIsSaving(true);
    try {
      const slug = generateSlug(newCategoryName);
      const maxOrder = Math.max(...categories.map(c => c.sort_order || 0), 0);
      
      const { error } = await supabase.from("categories").insert({
        name: newCategoryName.trim(),
        slug,
        parent_id: selectedParent === "none" ? null : selectedParent,
        sort_order: maxOrder + 1,
      });

      if (error) throw error;

      toast.success("Categoria creata!");
      setNewCategoryName("");
      setSelectedParent("none");
      fetchCategories();
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error("Errore nella creazione della categoria");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategory) return;

    try {
      // First delete article_categories referencing this category
      await supabase
        .from("article_categories")
        .delete()
        .eq("category_id", deleteCategory.id);

      // Delete child categories first
      const childCategories = categories.filter(c => c.parent_id === deleteCategory.id);
      for (const child of childCategories) {
        await supabase.from("article_categories").delete().eq("category_id", child.id);
        await supabase.from("categories").delete().eq("id", child.id);
      }

      const { error } = await supabase
        .from("categories")
        .delete()
        .eq("id", deleteCategory.id);

      if (error) throw error;

      toast.success("Categoria eliminata!");
      fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Errore nell'eliminazione della categoria");
    } finally {
      setDeleteCategory(null);
    }
  };

  const handleStartEdit = (category: Category) => {
    setEditingCategory(category);
    setEditName(category.name);
  };

  const handleSaveEdit = async () => {
    if (!editingCategory || !editName.trim()) return;

    try {
      const { error } = await supabase
        .from("categories")
        .update({
          name: editName.trim(),
          slug: generateSlug(editName),
        })
        .eq("id", editingCategory.id);

      if (error) throw error;

      toast.success("Categoria aggiornata!");
      setEditingCategory(null);
      setEditName("");
      fetchCategories();
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error("Errore nell'aggiornamento della categoria");
    }
  };

  const handleStartMove = (category: Category) => {
    setMovingCategory(category);
    setSelectedPosition("");
  };

  const handleConfirmMove = async () => {
    if (!movingCategory || !selectedPosition) return;

    const sameLevelCategories = categories
      .filter(c => c.parent_id === movingCategory.parent_id)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    const targetIndex = parseInt(selectedPosition);
    const currentIndex = sameLevelCategories.findIndex(c => c.id === movingCategory.id);

    if (targetIndex === currentIndex) {
      setMovingCategory(null);
      return;
    }

    try {
      // Reassign sort_order for all categories at this level
      const newOrder = [...sameLevelCategories];
      const [moved] = newOrder.splice(currentIndex, 1);
      newOrder.splice(targetIndex, 0, moved);

      // Update all sort_orders in database
      for (let i = 0; i < newOrder.length; i++) {
        await supabase
          .from("categories")
          .update({ sort_order: i })
          .eq("id", newOrder[i].id);
      }

      toast.success("Posizione aggiornata!");
      fetchCategories();
    } catch (error) {
      console.error("Error moving category:", error);
      toast.error("Errore nello spostamento della categoria");
    } finally {
      setMovingCategory(null);
    }
  };

  const getPositionOptions = (category: Category) => {
    const sameLevelCategories = categories
      .filter(c => c.parent_id === category.parent_id)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    
    return sameLevelCategories.map((c, index) => ({
      value: index.toString(),
      label: `${index + 1}. ${c.name}`,
      isCurrent: c.id === category.id
    }));
  };

  // Build hierarchical structure
  const buildTree = (cats: Category[]): Category[] => {
    const map = new Map<string, Category>();
    const roots: Category[] = [];

    cats.forEach((cat) => {
      map.set(cat.id, { ...cat, children: [] });
    });

    cats.forEach((cat) => {
      const node = map.get(cat.id)!;
      if (cat.parent_id && map.has(cat.parent_id)) {
        map.get(cat.parent_id)!.children!.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  };

  const categoryTree = buildTree(categories);

  // Get main categories (no parent) for the select dropdown
  const mainCategories = categories.filter((cat) => !cat.parent_id);

  const renderCategoryItem = (category: Category, level: number = 0, siblings: Category[] = []) => {
    const isEditing = editingCategory?.id === category.id;
    const currentIndex = siblings.findIndex(c => c.id === category.id);
    const canMoveUp = currentIndex > 0;
    const canMoveDown = currentIndex < siblings.length - 1;

    return (
      <div key={category.id}>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`flex items-center justify-between p-3 rounded-xl hover:bg-muted/50 transition-colors group`}
          style={{ marginLeft: level * 24 }}
        >
          <div className="flex items-center gap-2 flex-1">
            {level > 0 && (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
            <FolderTree className="w-4 h-4 text-primary" />
            
            {isEditing ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="h-8 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveEdit();
                    if (e.key === "Escape") setEditingCategory(null);
                  }}
                />
                <button
                  onClick={handleSaveEdit}
                  className="p-1.5 rounded-full text-green-500 hover:bg-green-500/10 transition-all"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setEditingCategory(null)}
                  className="p-1.5 rounded-full text-muted-foreground hover:bg-muted transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <span className="font-medium">{category.name}</span>
                {category.children && category.children.length > 0 && (
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    {category.children.length} sotto
                  </span>
                )}
              </>
            )}
          </div>
          
          {!isEditing && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleStartMove(category)}
                className="p-1.5 rounded-full text-blue-500 hover:bg-blue-500/10 transition-all"
                title="Sposta"
              >
                <ArrowUpDown className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleStartEdit(category)}
                className="p-1.5 rounded-full text-primary hover:bg-primary/10 transition-all"
                title="Modifica"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => setDeleteCategory(category)}
                className="p-1.5 rounded-full text-destructive hover:bg-destructive/10 transition-all"
                title="Elimina"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </motion.div>
        {category.children?.map((child) => renderCategoryItem(child, level + 1, category.children || []))}
      </div>
    );
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={onClose} shouldScaleBackground={false}>
        <DrawerContent className="h-[85vh] bg-background border-t border-border">
          <DrawerHeader className="relative border-b border-border">
            <button
              onClick={onClose}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <DrawerTitle className="text-xl font-bold text-center">
              Gestione Categorie
            </DrawerTitle>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Add New Category */}
            <div className="space-y-3 p-4 bg-muted/30 rounded-2xl">
              <h3 className="text-sm font-medium text-foreground">
                Nuova Categoria
              </h3>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Nome categoria"
              />
              <Select value={selectedParent} onValueChange={setSelectedParent}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Categoria principale (opzionale)" />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border">
                  <SelectItem value="none">Nessuna (categoria principale)</SelectItem>
                  {mainCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleAddCategory}
                disabled={isSaving}
                className="w-full gap-2"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Aggiungi Categoria
              </Button>
            </div>

            {/* Categories List */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-foreground px-1">
                Categorie esistenti
              </h3>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : categoryTree.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border border-dashed rounded-xl">
                  <FolderTree className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nessuna categoria creata</p>
                </div>
              ) : (
                <AnimatePresence>
                  {categoryTree.map((category) => renderCategoryItem(category, 0, categoryTree))}
                </AnimatePresence>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
        <AlertDialogContent className="bg-background border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare "{deleteCategory?.name}". 
              {deleteCategory?.children && deleteCategory.children.length > 0 && 
                " Questo eliminerà anche tutte le sotto-categorie."}
              {" "}Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Position Dialog */}
      <AlertDialog open={!!movingCategory} onOpenChange={() => setMovingCategory(null)}>
        <AlertDialogContent className="bg-background border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Sposta "{movingCategory?.name}"</AlertDialogTitle>
            <AlertDialogDescription>
              Seleziona la nuova posizione per questa categoria.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select value={selectedPosition} onValueChange={setSelectedPosition}>
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Seleziona posizione" />
              </SelectTrigger>
              <SelectContent className="bg-background border border-border">
                {movingCategory && getPositionOptions(movingCategory).map((option) => (
                  <SelectItem 
                    key={option.value} 
                    value={option.value}
                    className={option.isCurrent ? "font-bold text-primary" : ""}
                  >
                    {option.label} {option.isCurrent && "(attuale)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmMove}
              disabled={!selectedPosition}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Sposta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CategoryManager;
